import compact from 'lodash/compact'
import { SensorPayloadData } from 'pages/reporting/reports/rug/rugReportData'
import { useMemo } from 'react'
import {
  GeckoFormattingVariant,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

interface UseSensorPayloadStringsParams {
  payloads: SensorPayloadData[] | undefined
  formatMeasurement: (params: {
    measurement: GeckoMeasurement | undefined
    variant?: GeckoFormattingVariant | 'none'
    showLoading?: boolean
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  }) => string | undefined
}

interface SensorPayloadStrings {
  payloadTypesStr: string | undefined
  sensorFrequenciesStr: string | undefined
  sensorDiametersStr: string | undefined
}

export const useSensorPayloadStrings = ({
  payloads,
  formatMeasurement,
}: UseSensorPayloadStringsParams): SensorPayloadStrings => {
  const safePayloads = useMemo(() => payloads ?? [], [payloads])

  const payloadTypesStr = useMemo(() => {
    const types = compact(safePayloads.map((p) => p.payloadType))
    return types.length > 0 ? types.join(', ') : undefined
  }, [safePayloads])

  const sensorFrequenciesStr = useMemo(() => {
    const frequencies = compact(safePayloads.map((p) => p.sensorFrequency))
    return frequencies.length > 0
      ? frequencies.map((f) => `${f} MHz`).join(', ')
      : undefined
  }, [safePayloads])

  const sensorDiametersStr = useMemo(() => {
    const diameters = compact(
      safePayloads.map((p) => {
        if (p.sensorDiameter?.value !== undefined) {
          return formatMeasurement({
            measurement: GeckoMeasurement.CreateFromValue(
              p.sensorDiameter.value,
              GeckoMeasurementUnit.GetOrCreate(p.sensorDiameter.unit)
            ),
            variant: 'narrow',
            minimumFractionDigits: 2,
            maximumFractionDigits: 3,
          })
        }
        return undefined
      })
    )
    return diameters.length > 0 ? diameters.join(', ') : undefined
  }, [safePayloads, formatMeasurement])

  return {
    payloadTypesStr,
    sensorFrequenciesStr,
    sensorDiametersStr,
  }
}
